import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../icons/Icon.js';
import { useIsMobile } from '../runtime/hooks.js';
import { cx } from '../utils/cx.js';
import { Button, type ButtonVariant } from './Button.js';
import { Textarea } from './Input.js';
import { FormField } from './FormField.js';

/**
 * C-14 ボトムシート（モバイル）/ モーダル（デスクトップ）。角丸 20（上辺）。
 * モーダルの上にモーダルは置かない（禁止 #8）。確認は「シートの中身を差し替える」形で行う（ConfirmDialog）。
 */
export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'md' | 'lg' | 'full';
  /** 右端から開くサイドパネル。モバイルでは画面幅に合わせる。 */
  placement?: 'center' | 'right';
  /** 閉じても入力中の状態を維持する。子側の取得・既読処理は open に連動させる。 */
  keepMounted?: boolean;
  id?: string;
  closeLabel?: string;
  /** 処理中は閉じられない */
  busy?: boolean;
}

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function Dialog({ open, onClose, title, description, children, footer, size = 'md', placement = 'center', keepMounted = false, id, closeLabel = '閉じる', busy = false }: DialogProps) {
  const isMobile = useIsMobile();
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descId = useId();

  // フォーカスの移動と復帰・Escape・背面のスクロール抑止
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const first = panel?.querySelector<HTMLElement>('[data-autofocus]') ?? panel?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel)?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      // 添付の拡大などが開いている間は、一番手前のダイアログだけがキーを扱う。
      const visibleDialogs = Array.from(document.querySelectorAll<HTMLElement>('[role="dialog"]')).filter((node) => node.getClientRects().length > 0);
      if (visibleDialogs.at(-1) !== panel) return;
      if (e.key === 'Escape' && !busy) {
        e.stopPropagation();
        onClose();
      }
      if (e.key === 'Tab' && panel) {
        const nodes = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
        if (nodes.length === 0) return;
        const firstNode = nodes[0]!;
        const lastNode = nodes[nodes.length - 1]!;
        if (e.shiftKey && document.activeElement === firstNode) {
          e.preventDefault();
          lastNode.focus();
        } else if (!e.shiftKey && document.activeElement === lastNode) {
          e.preventDefault();
          firstNode.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      prev?.focus?.();
    };
  }, [open, onClose, busy]);

  if (!open && !keepMounted) return null;

  return createPortal(
    <div className={cx('fixed inset-0 z-50 flex', placement === 'right' ? 'items-stretch justify-end' : 'items-end justify-center md:items-center md:p-6')} style={open ? undefined : { display: 'none' }} inert={!open} aria-hidden={!open || undefined} role="presentation">
      <div className="absolute inset-0 bg-overlay" onClick={busy ? undefined : onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        id={id}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={cx(
          'relative flex w-full flex-col bg-card outline-none',
          'border-[var(--sheet-border-color)] shadow-elev-2',
          placement === 'right' ? 'ui-dialog-right h-dvh max-w-[min(100vw,34rem)] border-l pb-[env(safe-area-inset-bottom)]' : isMobile
            ? 'max-h-[92dvh] rounded-t-sheet border-t pb-[env(safe-area-inset-bottom)]'
            : cx('max-h-[90vh] rounded-sheet border', size === 'md' && 'max-w-xl', size === 'lg' && 'max-w-3xl', size === 'full' && 'max-w-5xl'),
        )}
      >
        {isMobile && placement !== 'right' ? <div className="mx-auto mt-2 h-1 w-10 rounded-pill bg-line-strong" aria-hidden="true" /> : null}
        <div className="flex items-start justify-between gap-3 px-5 pt-4">
          <div className="min-w-0">
            <h2 id={titleId} className="text-headline wrap-name">
              {title}
            </h2>
            {description ? (
              <p id={descId} className="mt-1 text-callout text-fg-secondary">
                {description}
              </p>
            ) : null}
          </div>
          <button type="button" onClick={onClose} disabled={busy} aria-label={closeLabel} className="-mr-2 -mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-pill text-fg-secondary hover:bg-sunken disabled:disabled-look">
            <Icon name="close" size={24} />
          </button>
        </div>
        <div className={cx('min-h-0 flex-1 overflow-y-auto px-5 py-4', placement === 'right' && 'flex flex-col')}>{children}</div>
        {footer ? <div className="flex flex-col-reverse gap-2 border-t border-line-strong px-5 py-4 sm:flex-row sm:justify-end">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

/**
 * C-16 確認ダイアログ。1 段階（承認・却下）。
 * requireComment: コメント必須（空なら送信ボタン無効 + 理由を文字で）。
 */
export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  /** 戻り値が Promise なら完了まで処理中にする */
  onConfirm: (comment: string) => unknown;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'primary' | 'danger';
  comment?: 'none' | 'optional' | 'required';
  commentLabel?: string;
  children?: ReactNode;
  /** 「承認は取り消せません」などの注意 */
  irreversibleNote?: ReactNode;
}

export function ConfirmDialog({ open, onClose, onConfirm, title, description, confirmLabel = '実行する', cancelLabel = 'やめる', tone = 'primary', comment = 'none', commentLabel = 'コメント', children, irreversibleNote }: ConfirmDialogProps) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setText('');
  }, [open]);
  const missing = comment === 'required' && text.trim() === '';
  const variant: ButtonVariant = tone === 'danger' ? 'danger' : 'primary';

  const submit = async () => {
    setBusy(true);
    try {
      await onConfirm(text.trim());
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      busy={busy}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button variant={variant} onClick={submit} loading={busy} disabled={missing} disabledReason={missing ? `${commentLabel}を入力してください` : undefined}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
      {comment !== 'none' ? (
        <FormField label={commentLabel} required={comment === 'required'} help={comment === 'optional' ? '任意' : undefined}>
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} data-autofocus />
        </FormField>
      ) : null}
      {irreversibleNote ? <p className="mt-3 text-caption text-fg-secondary">{irreversibleNote}</p> : null}
    </Dialog>
  );
}
